import { addDoc, collection, doc, getDoc, updateDoc, getDocs, query, where, deleteDoc, documentId, serverTimestamp, orderBy, limit, WhereFilterOp, startAfter, getCountFromServer, QueryDocumentSnapshot, DocumentData, increment } from "firebase/firestore";
import { createContext, useContext, useState } from "react";
import { db } from "../firebase";
import { getStorage, ref, deleteObject, listAll } from "firebase/storage";
import { itemsPerPage } from "../utils/fields";
import { generateSearchTerms } from "../utils/functions";
import { calculateBuyerScore, IBuyerScore } from "../utils/buyerScore";

interface WatchContextType {
    createWatch: (advertiser: string, brand: string, model: string, year: number, slug: string) => Promise<string | null>;
    getWatch: (watchId: string) => Promise<{ id: string, [key: string]: any } | null>;
    updateWatch: (watchId: string, infos: {}) => Promise<boolean | null>;
    deleteWatch: (userId: string | null, watchId: string) => Promise<boolean | null>;
    getWatches: (parameter: string, value: string, operator?: WhereFilterOp) => Promise<{ id: string, [key: string]: any }[] | null | []>;
    getWatchesByIds: (ids: string[]) => Promise<{ id: string, [key: string]: any }[] | null>;
    getCartWatches: (cart: string[]) => Promise<{ id: string, [key: string]: any }[] | null | []>;
    getAdvertiser: (advertiserId: string) => Promise<{ id: string, [key: string]: any } | null>;
    getAdvertiserBySlug: (slug: string) => Promise<{ id: string, [key: string]: any } | null>;
    getMyWatches: (advertiserId: string) => Promise<{ id: string, [key: string]: any }[] | null | []>;
    searchWatches: (filters: { field: string, operator: WhereFilterOp, value: string | string[] | number }[], price?: { from: number, to: number }, year?: { from: number, to: number }, last?: QueryDocumentSnapshot<DocumentData, DocumentData>) => Promise<{ total: number, data: { id: string, [key: string]: any }[], last: QueryDocumentSnapshot<DocumentData, DocumentData> } | null>;
    getWatchBySlug: (slug: string) => Promise<{} | null>;
    addToCart: (userId: string, watchId: string) => Promise<boolean | null>;
    removeFromCart: (userId: string, watchId: string) => Promise<boolean | null>;
    getCartItems: (userId: string) => Promise<boolean | null>;
    cart: string[] | null;
    cartUpdated: boolean;
    getPurchasedWatches: (userId: string) => Promise<{ id: string, [key: string]: any }[] | null>;
    getAdvertiserReviews: (advertiser: string) => Promise<{ id: string, rate: number, review: string, createdAt: Date, hidden: boolean }[] | null>;
    getPublicAdvertiserReviews: (advertiser: string) => Promise<{ id: string, rate: number, review: string, createdAt: Date, hidden: boolean }[] | null>;
    getAdvertiserWatches: (advertiserId: string) => Promise<{ id: string, [key: string]: any }[] | null | []>;
    getWatchViews: (watchId: string) => Promise<{ id: string, watch: string, count: number } | null>;
    countWatchView: (viewId: string) => Promise<true | null>;
    getTop10Watches: () => Promise<null>;
    getLatest10Watches: () => Promise<{ id: string, [key: string]: any }[] | null>;
    top10: { id: string, watch: string, count: number }[] | null;
    getBrands: () => Promise<{ brand: string, count: number }[] | null>;
    getBuyerScore: (userId: string) => Promise<IBuyerScore | null>;
    getUserByEmail: (email: string) => Promise<{ id: string, name: string, email: string, phoneNumber: string } | null>;
}

const WatchContext = createContext<WatchContextType | undefined>(undefined);

export const WatchProvider = ({ children }: { children: React.ReactNode }) => {

    const [cart, setCart] = useState<string[] | null>(null);
    const [cartUpdated, setCartUpdated] = useState(false);
    const [top10, setTop10] = useState<{ id: string, watch: string, count: number }[] | null>(null);

    async function addToCart(userId: string, watchId: string) {
        try {
            const q = query(collection(db, "carts"), where("userId", "==", userId));
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                await addDoc(collection(db, "carts"), {
                    userId, items: [watchId], createdAt: serverTimestamp()
                })
                return true;
            }
            const docSnap = docsSnap.docs[0];
            if (docSnap.data().items.includes(watchId)) {
                return null;
            }
            const docRef = doc(db, "carts", docSnap.id);
            await updateDoc(docRef, { items: [...docSnap.data().items, watchId] });
            setCartUpdated(false);
            return true;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function removeFromCart(userId: string, watchId: string) {
        try {
            const q = query(collection(db, "carts"), where("userId", "==", userId));
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                return null;
            }
            const docSnap = docsSnap.docs[0];
            if (!docSnap.data().items.includes(watchId)) {
                return null;
            }
            const docRef = doc(db, "carts", docSnap.id);
            await updateDoc(docRef, { items: [...docSnap.data().items.filter((item: string) => item !== watchId)] });
            setCartUpdated(false);
            return true;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getCartItems(userId: string) {
        try {
            const q = query(collection(db, "carts"), where("userId", "==", userId));
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                setCart([]);
                setCartUpdated(true);
                return true;
            }
            const docSnap = docsSnap.docs[0];
            setCart(docSnap.data().items);
            setCartUpdated(true);
            return true;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function createWatch(advertiser: string, brand: string, model: string, year: number, slug: string) {
        try {
            if (!brand || !model || !year || !slug) {
                return null;
            }
            const searchTerms = generateSearchTerms(brand, model);
            const docRef = await addDoc(collection(db, "watches"), {
                advertiser,
                brand: brand.toLowerCase().trim(),
                model: model.toLowerCase().trim(),
                year,
                slug,
                status: "draft",
                createdAt: serverTimestamp(),
                images: [],
                searchTerms
            })

            return docRef.id
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getWatch(watchId: string) {
        try {
            const docRef = doc(db, "watches", watchId);
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) {
                return null;
            }
            return { ...docSnap.data(), id: docSnap.id };
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function updateWatch(watchId: string, infos: { [key: string]: any }) {
        try {
            const docRef = doc(db, "watches", watchId);
            const loweredInfos: { [key: string]: any } = {};
            for (let key in infos) {
                if (typeof infos[key] === "string" && ["brand", "model"].includes(key)) {
                    loweredInfos[key] = infos[key].toLowerCase().trim();
                } else {
                    loweredInfos[key] = infos[key];
                }
            }

            const currentDoc = await getDoc(docRef);
            if (currentDoc.exists()) {
                const currentData = currentDoc.data();

                if (loweredInfos.brand || loweredInfos.model) {
                    const finalBrand = loweredInfos.brand || currentData.brand || "";
                    const finalModel = loweredInfos.model || currentData.model || "";

                    loweredInfos.searchTerms = generateSearchTerms(
                        finalBrand, finalModel
                    );
                }

            }

            await updateDoc(docRef, loweredInfos);
            return true;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function deleteWatch(userId: string | null, watchId: string) {
        try {
            if (!userId) {
                return null;
            }

            // excluir imagens do relógio
            const storage = getStorage();
            const folderRef = ref(storage, `watches/${userId}/${watchId}`);
            const result = await listAll(folderRef);

            const deletePromises = result.items.map((itemRef) => {
                return deleteObject(itemRef);
            })

            await Promise.all(deletePromises);

            // excluir documento do relógio
            const docRef = doc(db, "watches", watchId);
            await deleteDoc(docRef);

            return true;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getWatches(parameter: string, value: string, operator: WhereFilterOp = "==") {
        try {
            const docs: { id: string;[key: string]: any }[] = [];
            const q = query(collection(db, "watches"), where(parameter, operator, value));
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                return null;
            }
            docsSnap.forEach(document => {
                docs.push({
                    id: document.id,
                    ...document.data()
                })
            })
            return docs;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getWatchesByIds(ids: string[]) {
        try {
            if (!ids.length) {
                return null;
            }
            const docs: { id: string;[key: string]: any }[] = [];
            const q = query(collection(db, "watches"), where("__name__", "in", ids), where("status", "==", "display"));
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                return null;
            }
            docsSnap.forEach(document => {
                docs.push({
                    id: document.id,
                    ...document.data()
                })
            })
            return docs;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getCartWatches(cart: string[]) {
        try {
            const docs: { id: string;[key: string]: any }[] = [];
            const q = query(collection(db, "watches"), where("status", "==", "display"), where(documentId(), "in", cart));
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                return null;
            }
            docsSnap.forEach(document => {
                docs.push({
                    id: document.id,
                    slug: document.data().slug,
                    cImgUrl: document.data().images?.length ? document.data().images[0].cImgUrl : undefined,
                    brand: document.data().brand,
                    model: document.data().model,
                    year: document.data().year,
                    price: document.data().price,
                })
            })
            return docs;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getAdvertiserWatches(advertiserId: string) {
        try {
            const docs: { id: string;[key: string]: any }[] = [];
            const q = query(collection(db, "watches"), where("status", "==", "display"), where("advertiser", "==", advertiserId));
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                return null;
            }
            docsSnap.forEach(document => {
                docs.push({
                    id: document.id,
                    slug: document.data().slug,
                    cImgUrl: document.data().images?.length ? document.data().images[0].cImgUrl : undefined,
                    brand: document.data().brand,
                    model: document.data().model,
                    year: document.data().year,
                    price: document.data().price,
                })
            })
            return docs;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getPurchasedWatches(userId: string) {
        try {
            const docs: { id: string;[key: string]: any }[] = [];
            const q = query(collection(db, "watches"), where("status", "==", "sold"), where("buyer", "==", userId));
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                return null;
            }
            docsSnap.forEach(document => {
                docs.push({
                    id: document.id,
                    slug: document.data().slug,
                    cImgUrl: document.data().images?.length ? document.data().images[0].cImgUrl : undefined,
                    brand: document.data().brand,
                    model: document.data().model,
                    year: document.data().year,
                    price: document.data().price,
                    advertiser: document.data().advertiser
                })
            })
            return docs;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function searchWatches(filters: { field: string, operator: WhereFilterOp, value: string | string[] | number }[], price?: { from: number, to: number }, year?: { from: number, to: number }, last?: QueryDocumentSnapshot<DocumentData, DocumentData>) {
        try {
            let docs: { id: string;[key: string]: any }[] = [];
            let q = query(collection(db, "watches"), where("status", "==", "display"));
            if (!price && !year) {
                q = query(q, limit(itemsPerPage));
            }
            let totalQuery = query(collection(db, "watches"), where("status", "==", "display"));
            if (last) {
                q = query(q, startAfter(last));
            }
            for (let i = 0; i < filters.length; i++) {
                const currentFilter = filters[i];
                q = query(q, where(currentFilter.field, currentFilter.operator, currentFilter.value));
                totalQuery = query(totalQuery, where(currentFilter.field, currentFilter.operator, currentFilter.value));
            }
            const total = await getCountFromServer(totalQuery);
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                return null;
            }
            docsSnap.forEach(document => {
                docs.push({
                    id: document.id,
                    brand: document.data().brand,
                    model: document.data().model,
                    year: document.data().year,
                    price: document.data().price,
                    slug: document.data().slug,
                    cImgUrl: document.data().images?.length ? document.data().images[0].cImgUrl : undefined,
                })
            })
            if (price?.from) {
                docs = docs.filter(d => d.price >= price.from);
            }
            if (price?.to) {
                docs = docs.filter(d => d.price <= price.to);
            }
            if (year?.from) {
                docs = docs.filter(d => d.year >= year.from);
            }
            if (year?.to) {
                docs = docs.filter(d => d.year <= year.to);
            }
            return { total: total.data().count, data: docs, last: docsSnap.docs[docsSnap.docs.length - 1] };
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getAdvertiser(advertiserId: string) {
        try {
            if (!advertiserId) {
                return null;
            }
            const userRef = doc(db, "users", advertiserId);
            const userSnap = await getDoc(userRef);
            if (!userSnap.exists()) {
                return null;
            }
            return { ...userSnap.data(), id: userSnap.id };
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getAdvertiserBySlug(slug: string) {
        try {
            let docs: { id: string, [key: string]: any } = { id: "none" };
            const q = query(collection(db, "users"), where("slug", "==", slug));
            const docSnap = await getDocs(q);
            if (docSnap.empty) {
                return null;
            }

            docSnap.forEach(document => {
                docs = {
                    id: document.id,
                    createdAt: document.data().createdAt?.toDate(),
                    slug: document.data().slug,
                    name: document.data().name,
                    email: document.data().email,
                    phoneNumber: document.data().phoneNumber,
                    description: document.data().description,
                    instagram: document.data().instagram,
                    facebook: document.data().facebook,
                    site: document.data().site
                }
            })
            return docs;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getAdvertiserReviews(advertiser: string) {
        try {
            let docs: { id: string, rate: number, review: string, createdAt: Date, hidden: boolean }[] = [];
            const q = query(collection(db, "reviews"), where("advertiser", "==", advertiser));
            const docSnap = await getDocs(q);
            if (docSnap.empty) {
                return null;
            }

            docSnap.forEach(document => {
                docs.push(
                    {
                        id: document.id,
                        rate: document.data().rate,
                        review: document.data().review,
                        createdAt: document.data().createdAt?.toDate(),
                        hidden: document.data().hidden
                    }
                )
            })
            return docs;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getPublicAdvertiserReviews(advertiser: string) {
        try {
            let docs: { id: string, rate: number, review: string, createdAt: Date, hidden: boolean }[] = [];
            const q = query(collection(db, "reviews"), where("advertiser", "==", advertiser), where("hidden", "==", false));
            const docSnap = await getDocs(q);
            if (docSnap.empty) {
                return null;
            }

            docSnap.forEach(document => {
                docs.push(
                    {
                        id: document.id,
                        rate: document.data().rate,
                        review: document.data().review,
                        createdAt: document.data().createdAt?.toDate(),
                        hidden: document.data().hidden
                    }
                )
            })
            return docs;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getMyWatches(advertiserId: string) {
        try {
            const docs: { id: string;[key: string]: any }[] = [];
            const q = query(collection(db, "watches"), where("advertiser", "==", advertiserId));
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                return null;
            }
            docsSnap.forEach(document => {
                docs.push({
                    id: document.id,
                    ...document.data()
                })
            })
            return docs;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getWatchBySlug(slug: string) {
        try {
            let docs = {}
            const q = query(collection(db, "watches"), where("slug", "==", slug));
            const docSnap = await getDocs(q);
            if (docSnap.empty) {
                return null;
            }

            docSnap.forEach(document => {
                docs = {
                    id: document.id,
                    status: document.data().status,
                    brand: document.data().brand,
                    model: document.data().model,
                    year: document.data().year,
                    price: document.data().price,
                    advertiser: document.data().advertiser,
                    description: document.data().description,
                    braceletMaterial: document.data().braceletMaterial,
                    caseMaterial: document.data().caseMaterial,
                    content: document.data().content,
                    diameter: document.data().diameter,
                    functions: document.data().functions,
                    glass: document.data().glass,
                    movement: document.data().movement,
                    movementModel: document.data().movementModel,
                    slug: document.data().slug,
                    state: document.data().state,
                    tightness: document.data().tightness,
                    boxAndDocuments: document.data().boxAndDocuments,
                    images: document.data().images
                }
            })
            return docs;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getWatchViews(watchId: string) {
        try {
            const q = query(collection(db, "watchesviews"), where("watch", "==", watchId));
            const docSnap = await getDocs(q);
            if (docSnap.empty) {
                const docRef = await addDoc(collection(db, "watchesviews"), {
                    watch: watchId,
                    count: 0
                })

                return {
                    id: docRef.id,
                    watch: watchId,
                    count: 0
                }
            }
            const docao = docSnap.docs[0];
            return {
                id: docao.id,
                watch: docao?.data()?.watch,
                count: docao?.data()?.count
            }
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    // incremento atômico no servidor: duas visualizações simultâneas não se sobrescrevem
    async function countWatchView(viewId: string) {
        try {
            const docRef = doc(db, "watchesviews", viewId);
            await updateDoc(docRef, { count: increment(1) });
            return true;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    // O contador de visualizações nunca é zerado, então relógios vendidos/escondidos continuam no topo de
    // watchesviews. Em vez de pegar só os 10 primeiros (e perder as vagas ocupadas por eles), percorre o
    // ranking em páginas até juntar 10 relógios em exibição — com teto de páginas para limitar leituras.
    async function getTop10Watches() {
        const viewsRef = collection(db, "watchesviews");
        const TOP_SIZE = 10;
        const PAGE_SIZE = 20; // abaixo do limite de 30 valores do "in" usado em getWatchesByIds
        const MAX_PAGES = 5;

        try {
            const displayedWatches: { id: string, watch: string, count: number }[] = [];
            let last: QueryDocumentSnapshot<DocumentData, DocumentData> | undefined;
            for (let page = 0; page < MAX_PAGES && displayedWatches.length < TOP_SIZE; page++) {
                const q = last ?
                    query(viewsRef, orderBy("count", "desc"), startAfter(last), limit(PAGE_SIZE))
                    : query(viewsRef, orderBy("count", "desc"), limit(PAGE_SIZE));
                const querySnapshot = await getDocs(q);
                if (querySnapshot.empty) {
                    break;
                }
                last = querySnapshot.docs[querySnapshot.docs.length - 1];
                const pageDocs = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    watch: doc.data().watch,
                    count: doc.data().count
                }));
                const watches = await getWatchesByIds(pageDocs.map(d => d.watch));
                const watchesIds = watches?.map(w => w.id) || [];
                displayedWatches.push(...pageDocs.filter(t => watchesIds.includes(t.watch)));
                if (querySnapshot.docs.length < PAGE_SIZE) {
                    break;
                }
            }
            setTop10(displayedWatches.slice(0, TOP_SIZE).sort((a: { count: number }, b: { count: number }) => a.count - b.count));
            return null;
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    async function getLatest10Watches() {
        const bimbaCollectionRef = collection(db, "watches");
        const q = query(bimbaCollectionRef, where("status", "==", "display"), orderBy("createdAt", "desc"), limit(10));

        try {
            const querySnapshot = await getDocs(q);
            if (querySnapshot.empty) {
                return null;
            }
            const latest10Docs = querySnapshot.docs.map(document => ({
                id: document.id,
                status: document.data().status,
                brand: document.data().brand,
                model: document.data().model,
                year: document.data().year,
                price: document.data().price,
                slug: document.data().slug,
                images: document.data().images,
                createdAt: document.data().createdAt,
                isBanner: document.data().isBanner
            }));
            return latest10Docs;
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    const getBrands = async () => {
        try {
            if (!top10?.length) {
                return null;
            }

            const top10Ids = top10?.map(t => t.watch);
            const watches = await getDocs(query(collection(db, "watches"), where(documentId(), "in", top10Ids)));
            if (watches.empty) {
                return null;
            }
            const docs = watches.docs;
            const brandsInfo: { brand: string, count: number }[] = [];
            for (let i = 0; i < docs.length; i++) {
                const brandName = docs[i].data().brand;
                if (!brandName) continue;
                const existingBrand = brandsInfo.find(b => b.brand === brandName);
                const count = top10?.find(t => t.watch === docs[i].id)?.count;

                if (count === undefined || count === null) {
                    continue;
                }

                if (!existingBrand) {
                    brandsInfo.push({
                        brand: brandName,
                        count: count
                    });
                } else {
                    existingBrand.count += count;
                }
            }
            const brandzadas = brandsInfo.sort((a, b) => b.count - a.count).slice(0, 4);
            return brandzadas
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getBuyerScore(userId: string) {
        try {
            const userSnap = await getDoc(doc(db, "users", userId));
            if (!userSnap.exists()) {
                return null;
            }
            const userData = userSnap.data();

            const purchasesQuery = query(collection(db, "watches"), where("status", "==", "sold"), where("buyer", "==", userId));
            const reviewsQuery = query(collection(db, "reviews"), where("userId", "==", userId));

            const [purchasesSnap, reviewsSnap] = await Promise.all([
                getCountFromServer(purchasesQuery),
                getCountFromServer(reviewsQuery),
            ]);

            return calculateBuyerScore({
                emailVerified: !!userData.emailVerified,
                phoneVerified: !!userData.phoneVerified,
                createdAt: userData.createdAt?.toDate ? userData.createdAt.toDate() : null,
                purchasesCount: purchasesSnap.data().count,
                reviewsCount: reviewsSnap.data().count,
            });
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function getUserByEmail(email: string) {
        try {
            const q = query(collection(db, "users"), where("email", "==", email));
            const docsSnap = await getDocs(q);
            const document = docsSnap.docs.find(d => d.data().email === email);
            if (!document?.exists()) {
                return null;
            }
            const data = document.data();
            return {
                id: document.id,
                name: data.name,
                email: data.email,
                phoneNumber: data.phoneNumber,
            };
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    return (
        <WatchContext.Provider value={
            {
                createWatch, getWatch,
                updateWatch, deleteWatch,
                getWatches, getWatchesByIds, getMyWatches,
                getCartWatches, getAdvertiserBySlug,
                getAdvertiser, searchWatches,
                getWatchBySlug, addToCart,
                getCartItems, removeFromCart,
                cart, cartUpdated, getPurchasedWatches,
                getAdvertiserReviews, getAdvertiserWatches,
                getWatchViews, countWatchView, getTop10Watches,
                getLatest10Watches, top10, getBrands, getPublicAdvertiserReviews,
                getBuyerScore, getUserByEmail
            }
        }>
            {children}
        </WatchContext.Provider>
    )
}

export const useWatches = (): WatchContextType => {
    const context = useContext(WatchContext);
    if (context === undefined) {
        throw new Error('useWatches must be used within a WatchProvider');
    }
    return context;
};

export default WatchContext;
